import mixpanel from 'mixpanel-browser';

const ENV_CHECK = import.meta.env.PROD && import.meta.env.VITE_MIXPANEL_TOKEN;
const MIXPANEL_PROXY = 'https://monitor.tinuiti.com';

if (ENV_CHECK) {
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN || '', {
    api_host: MIXPANEL_PROXY
  });
}

// Event sub-groups: each group co-locates event names, their shared prop shape,
// and the derived union type. Spread into MxE below.

const floatingPanelEvents = {
  FloatingPanelOpenSidepanel: 'floating_panel_open_sidepanel',
  FloatingPanelCloseSidepanel: 'floating_panel_close_sidepanel',
  FloatingPanelMinimize: 'floating_panel_minimize',
  FloatingPanelReset: 'floating_panel_reset',
  FloatingPanelExpand: 'floating_panel_expand',
  FloatingPanelDragReposition: 'floating_panel_drag_reposition',
  FloatingPanelDragResize: 'floating_panel_drag_resize'
} as const;
interface WidgetActionProps {
  widget: string;
}
type FloatingPanelEvent = (typeof floatingPanelEvents)[keyof typeof floatingPanelEvents];

const fileDownloadEvents = {
  CsvDownload: 'csv_download',
  ExcelDownload: 'excel_download',
  HowtoVideoStarted: 'howto_video_started',
  HowtoVideoCompleted: 'howto_video_completed'
} as const;
interface FileDownloadProps {
  fileName: string;
}
type FileDownloadEvent = (typeof fileDownloadEvents)[keyof typeof fileDownloadEvents];

const optionSelectEvents = {
  DateGroupingChange: 'date_grouping_change',
  MetricOptionChange: 'metric_option_change',
  PageKpiChange: 'page_kpi_change',
  PagePresetChange: 'page_preset_change'
} as const;
interface OptionSelectProps {
  widget: string;
  option_selected: string | number | boolean;
}
type OptionSelectEvent = (typeof optionSelectEvents)[keyof typeof optionSelectEvents];

const noPropsEvents = {
  RefreshCcViews: 'refresh_cross_channel_views',
  SlideGeneratorGeneratePresentation: 'slide_generator_generate_presentation',
  SlideGeneratorSaveEdits: 'slide_generator_save_edits',
  SlideGeneratorSaveAsNewTemplate: 'slide_generator_save_as_new_template'
} as const;
type NoPropsEvent = (typeof noPropsEvents)[keyof typeof noPropsEvents];

const entityMutationEvents = {
  RecordCreated: 'record_created',
  RecordSaved: 'record_saved',
  RecordDeleted: 'record_deleted'
} as const;
type EntityMutationEvent = (typeof entityMutationEvents)[keyof typeof entityMutationEvents];

// Prop shapes for singleton events (unique shapes, not shared across a group)
interface PerformanceProps {
  metric_name: string;
  metric_value: number;
  rating: string;
  page: string;
}

interface PageLoadProps {
  metric_value: number;
  page: string;
}

interface FetchFailedProps {
  service: string;
  endpoint: string;
  method: string;
  error_type: string;
  page: string;
  status?: number;
  attempts?: number;
  online?: boolean;
  visibility?: string;
  connection?: string;
  error_message?: string;
  paused_for_visibility?: boolean;
}

// One per completed YouTube creator search, to measure how often creators share viewer data.
interface CreatorSearchResultsProps {
  page: string;
  search_mode: string;
  creators: number;
  sharing_viewer_data: number;
  local_creators: number;
}

// Mirrors reportError()'s (src/utils/errorReporting.ts) throttled Sentry reports, so patterns in
// every error that already goes to Sentry — the ErrorBoundary, the global React Query safety net,
// explicit reportError() call sites, and truly uncaught window errors/rejections — are also
// queryable/breakdown-able here the same way lambda_fetch_failed already is, instead of being
// visible only one incident at a time in Sentry.
interface ClientErrorProps {
  operation: string;
  error_type: string;
  page: string;
  error_message?: string;
  occurrence_count: number;
}

export const MxE = {
  ...floatingPanelEvents,
  ...fileDownloadEvents,
  ...optionSelectEvents,
  ...noPropsEvents,
  ...entityMutationEvents,
  PngDownload: 'png_download',
  GraphOrTableChange: 'graph_or_table_change',
  DateChange: 'date_change',
  WebVital: 'web_vital',
  PageDataLoad: 'page_data_load',
  LambdaFetchFailed: 'lambda_fetch_failed',
  ClientErrorReported: 'client_error_reported',
  TabChange: 'tab_change',
  ActionTriggered: 'action_triggered',
  ModalOpened: 'modal_opened',
  CreatorSearchResults: 'creator_search_results'
} as const;

export type MxEType = (typeof MxE)[keyof typeof MxE];

type EventProps = Record<FloatingPanelEvent, WidgetActionProps> &
  Record<FileDownloadEvent, FileDownloadProps> & {
    [MxE.PngDownload]: FileDownloadProps & { label: string };
  } & Record<OptionSelectEvent, OptionSelectProps> & {
    [MxE.GraphOrTableChange]: OptionSelectProps & { option_selected: 'graph' | 'table' };
  } & { [MxE.DateChange]: { primary_or_comparison: 'primary' | 'comparison' | 'both' } } & {
    [MxE.WebVital]: PerformanceProps;
  } & { [MxE.PageDataLoad]: PageLoadProps } & {
    [MxE.LambdaFetchFailed]: FetchFailedProps;
  } & { [MxE.ClientErrorReported]: ClientErrorProps } & Record<
    NoPropsEvent,
    Record<string, never>
  > & {
    [MxE.TabChange]: { page: string; option_selected: string };
  } & Record<EntityMutationEvent, { page: string; entity: string }> & {
    [MxE.ActionTriggered]: { page: string; action: string };
  } & { [MxE.ModalOpened]: { page: string; modal: string } } & {
    [MxE.CreatorSearchResults]: CreatorSearchResultsProps;
  };

interface MixpanelRegisterProps {
  company?: any | null;
  active_page?: string | null;
  page_group?: string | null;
}

export const Mixpanel = {
  identify: (id: string): void => {
    if (ENV_CHECK) {
      mixpanel.identify(id);
    }
  },
  track: <E extends MxEType>(
    name: E,
    ...args: EventProps[E] extends Record<string, never>
      ? [props?: Record<string, never>]
      : [props: EventProps[E]]
  ): void => {
    if (ENV_CHECK) {
      mixpanel.track(name, args[0] ?? {});
    }
  },
  track_pageview: (props: Record<string, any>): void => {
    if (ENV_CHECK) {
      mixpanel.track_pageview(props);
    }
  },
  people: {
    set: (props: Record<string, any>): void => {
      if (ENV_CHECK) {
        mixpanel.people.set(props);
      }
    },
    set_once: (props: Record<string, any>): void => {
      if (ENV_CHECK) {
        mixpanel.people.set_once(props);
      }
    }
  },
  register: (props: MixpanelRegisterProps): void => {
    if (ENV_CHECK) {
      mixpanel.register(props);
    }
  },
  unregister: (prop: string): void => {
    if (ENV_CHECK) {
      mixpanel.unregister(prop);
    }
  },
  register_once: (props: Record<string, any>): void => {
    if (ENV_CHECK) {
      mixpanel.register_once(props);
    }
  }
};
